<script setup>
// Dialog para configurar Stage#automation_hooks (Fase 1.5).
// Cada hook tem { on, action, params } - hoje só suportamos on:'enter'.
// Ações suportadas (espelha Leads::StageAutomationExecutor::SUPPORTED_ACTIONS):
//   apply_follow_up_template, assign_team, assign_owner,
//   set_classification, add_label, private_note
import { ref, computed, watch } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import FollowUpTemplatesAPI from 'dashboard/api/followUpTemplates';
import { useStoreGetters } from 'dashboard/composables/store';

const props = defineProps({
  stage: { type: Object, default: null },
});
const emit = defineEmits(['save']);

const dialogRef = ref(null);
const hooks = ref([]);
const templates = ref([]);
const isSaving = ref(false);

const getters = useStoreGetters();
const agents = computed(() => getters['agents/getAgents'].value || []);
const teams = computed(() => getters['teams/getTeams'].value || []);

const ACTIONS = [
  { key: 'apply_follow_up_template', label: 'Aplicar modelo de follow-up', icon: 'i-lucide-list-checks',
    paramsTemplate: { template_id: null } },
  { key: 'assign_team',              label: 'Atribuir ao time',           icon: 'i-lucide-users-round',
    paramsTemplate: { team_id: null } },
  { key: 'assign_owner',             label: 'Atribuir a um agente',       icon: 'i-lucide-user',
    paramsTemplate: { user_id: null } },
  { key: 'set_classification',       label: 'Definir classificação',      icon: 'i-lucide-flame',
    paramsTemplate: { value: 'warm' } },
  { key: 'add_label',                label: 'Adicionar etiqueta',         icon: 'i-lucide-tag',
    paramsTemplate: { labels: [] } },
  { key: 'private_note',             label: 'Escrever nota privada',      icon: 'i-lucide-sticky-note',
    paramsTemplate: { body: '' } },
];

defineExpose({
  open() {
    hooks.value = JSON.parse(JSON.stringify(props.stage?.automation_hooks || []));
    fetchAuxData();
    dialogRef.value?.open();
  },
  close() { dialogRef.value?.close(); },
});

async function fetchAuxData() {
  try {
    const { data } = await FollowUpTemplatesAPI.index({ active: 'true' });
    templates.value = data || [];
  } catch {
    templates.value = [];
  }
}

watch(() => props.stage, () => {
  if (props.stage) hooks.value = JSON.parse(JSON.stringify(props.stage.automation_hooks || []));
});

function addHook(action) {
  hooks.value.push({
    on: 'enter',
    action: action.key,
    params: JSON.parse(JSON.stringify(action.paramsTemplate)),
  });
}

function removeHook(idx) {
  hooks.value.splice(idx, 1);
}

function actionMeta(key) {
  return ACTIONS.find(a => a.key === key) || { label: key, icon: 'i-lucide-zap' };
}

// Opções derivadas pros SelectInput dos hooks (template/team/owner/classification).
const templateOptions = computed(() =>
  templates.value.map(t => ({ value: t.id, label: t.name }))
);
const teamOptions = computed(() =>
  teams.value.map(t => ({ value: t.id, label: t.name }))
);
const agentOptions = computed(() =>
  agents.value.map(u => ({ value: u.id, label: u.name }))
);
const classificationOptions = [
  { value: 'hot',  label: 'Quente' },
  { value: 'warm', label: 'Morno' },
  { value: 'cold', label: 'Frio' },
];

async function save() {
  if (isSaving.value) return;
  isSaving.value = true;
  try {
    emit('save', hooks.value);
    dialogRef.value?.close();
  } finally {
    isSaving.value = false;
  }
}

// CSV → array de labels
function labelsString(arr) {
  return Array.isArray(arr) ? arr.join(', ') : '';
}
function setLabelsString(hook, str) {
  hook.params.labels = str.split(',').map(s => s.trim()).filter(Boolean);
}
</script>

<template>
  <Dialog ref="dialogRef" width="3xl" :show-cancel-button="false" :show-confirm-button="false">
    <div class="flex flex-col gap-4">
      <div>
        <h3 class="text-heading-3 text-n-slate-12 mb-1">
          Automações · {{ stage?.name }}
        </h3>
        <p class="text-body-small text-n-slate-11 mb-0">
          Ações executadas automaticamente quando o lead entra neste estágio.
        </p>
      </div>

      <!-- Hooks existentes -->
      <ol v-if="hooks.length" class="flex flex-col gap-2">
        <li
          v-for="(hook, idx) in hooks"
          :key="`hook-${idx}`"
          class="rounded-xl border border-n-weak p-3 flex flex-col gap-2"
        >
          <header class="flex items-center justify-between gap-2">
            <div class="flex items-center gap-2 min-w-0">
              <i :class="actionMeta(hook.action).icon" class="size-4 text-n-brand" />
              <span class="text-body-main font-semibold text-n-slate-12 truncate">
                {{ actionMeta(hook.action).label }}
              </span>
              <span class="text-label-small uppercase px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-11">
                quando entrar
              </span>
            </div>
            <NextButton faded ruby size="sm" icon="i-lucide-trash-2" @click="removeHook(idx)" />
          </header>

          <div class="pl-6">
            <!-- Apply follow-up template -->
            <div v-if="hook.action === 'apply_follow_up_template'">
              <label class="block text-body-small font-medium text-n-slate-12 mb-1">Modelo</label>
              <SelectInput
                v-model="hook.params.template_id"
                :options="templateOptions"
                placeholder="- selecione -"
                class="w-full [&>select]:w-full"
              />
            </div>

            <!-- Assign team -->
            <div v-else-if="hook.action === 'assign_team'">
              <label class="block text-body-small font-medium text-n-slate-12 mb-1">Time</label>
              <SelectInput
                v-model="hook.params.team_id"
                :options="teamOptions"
                placeholder="- selecione -"
                class="w-full [&>select]:w-full"
              />
            </div>

            <!-- Assign owner -->
            <div v-else-if="hook.action === 'assign_owner'">
              <label class="block text-body-small font-medium text-n-slate-12 mb-1">Agente</label>
              <SelectInput
                v-model="hook.params.user_id"
                :options="agentOptions"
                placeholder="- selecione -"
                class="w-full [&>select]:w-full"
              />
            </div>

            <!-- Set classification -->
            <div v-else-if="hook.action === 'set_classification'">
              <label class="block text-body-small font-medium text-n-slate-12 mb-1">Classificação</label>
              <SelectInput
                v-model="hook.params.value"
                :options="classificationOptions"
                class="w-full [&>select]:w-full"
              />
            </div>

            <!-- Add label -->
            <div v-else-if="hook.action === 'add_label'">
              <label class="block text-body-small font-medium text-n-slate-12 mb-1">
                Etiquetas (separadas por vírgula)
              </label>
              <input
                type="text"
                :value="labelsString(hook.params.labels)"
                placeholder="ex: pos_visita, quente"
                class="w-full rounded-md border border-n-weak bg-n-solid-1 px-2 py-1.5 text-body-main"
                @input="setLabelsString(hook, $event.target.value)"
              />
            </div>

            <!-- Private note -->
            <div v-else-if="hook.action === 'private_note'">
              <label class="block text-body-small font-medium text-n-slate-12 mb-1">Texto da nota</label>
              <textarea
                v-model="hook.params.body"
                rows="2"
                placeholder="Mensagem que aparecerá como nota privada na conversa..."
                class="w-full rounded-md border border-n-weak bg-n-solid-1 px-2 py-1.5 text-body-main"
              />
            </div>
          </div>
        </li>
      </ol>
      <p v-else class="text-body-main text-n-slate-11">Nenhuma automação configurada.</p>

      <!-- Adicionar nova automação -->
      <div class="rounded-xl border border-dashed border-n-weak p-3">
        <p class="text-label-small font-semibold text-n-slate-12 uppercase tracking-wide mb-2">Adicionar automação</p>
        <div class="grid grid-cols-2 md:grid-cols-3 gap-2">
          <button
            v-for="a in ACTIONS"
            :key="a.key"
            type="button"
            class="text-left p-2 rounded-lg border border-n-weak hover:border-n-strong hover:bg-n-alpha-1 transition-colors flex items-center gap-2"
            @click="addHook(a)"
          >
            <i :class="a.icon" class="size-4 text-n-slate-12" />
            <span class="text-body-small text-n-slate-12">{{ a.label }}</span>
          </button>
        </div>
      </div>

      <div class="flex justify-end gap-2 pt-2">
        <NextButton faded slate label="Cancelar" @click="dialogRef?.close()" />
        <NextButton :is-loading="isSaving" label="Salvar automações" @click="save" />
      </div>
    </div>
  </Dialog>
</template>
